import styled from "styled-components";

/* ─── Layout ─────────────────────────────────────────────────────────────── */

export const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

export const ContentContainer = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  padding: 0 4px;
  overflow: visible;
`;
export const CollectionFilterWrap = styled.div`
  position: relative;
  z-index: 2;
  padding: 0 16px;
`;

/* Toolbar — single wrapper that aligns TabsComponent + CollectionFilter
   at the same horizontal width and padding (matches HomePage Toolbar).
   Vertical margins must mirror HomePage's Toolbar so the shared FeedList
   (margin-top: 110px; height: calc(100dvh - 150px)) lands on the same
   bottom edge — otherwise the footer peeks below the feed in feed view. */
export const Toolbar = styled.div`
  position: relative;
  width: 100%;
  max-width: 1080px;
  margin: 20px auto 0;
  padding: 0 16px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 12px;
  z-index: 3;
  margin-bottom: ${(p) => (p.$feed ? "0px" : "20px")};

  @media (max-width: 768px) {
    margin: ${(p) => (p.$feed ? "0" : "16px auto 0")};
    margin-bottom: ${(p) => (p.$feed ? "0px" : "16px")};
    padding: 0 12px;
  }
  @media (max-width: 400px) {
    margin-bottom: 0;
  }
`;

export const PaginationWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  margin-bottom: 0.8rem;
  padding: 24px 0;
`;

/* ─── Typography ─────────────────────────────────────────────────────────── */

export const ButtonText = styled.p`
  color: var(--color-brand-700);
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 600;
  line-height: normal;
  white-space: nowrap;
  letter-spacing: -0.005em;
  cursor: ${(p) => (p.$click ? "pointer" : "default")};

  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

/* ─── Bulk-action bars ──────────────────────────────────────────────────── */

export const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;

  @media (max-width: 768px) {
    gap: 8px;
  }
`;

/* Desktop bulk-action toolbar — cohesive frosted water pill instead of
   a loose floating row. Left cluster: Select All + Clear. Right cluster:
   Delete + Download. A soft aqua divider separates them. Breathes away
   from the filter above via a proper top margin. */
export const BigRow = styled.div`
  width: 100%;
  max-width: min(96dvw, 820px);
  margin: 4px auto 20px;
  padding: 8px 14px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-radius: 999px;
  border: 1px solid rgba(var(--glass-white-rgb), 0.7);
  background: radial-gradient(
    120% 100% at 20% 0%,
    rgba(var(--glass-white-rgb), 0.96) 0%,
    rgba(var(--glass-pale-rgb), 0.84) 55%,
    rgba(var(--glass-pale-rgb), 0.72) 100%
  );
  box-shadow:
    0 10px 24px rgba(var(--color-brand-500-rgb), 0.14),
    inset 0 1px 0 rgba(255, 255, 255, 0.95),
    inset 0 -3px 10px rgba(var(--color-brand-200-rgb), 0.22);

  @media (max-width: 768px) {
    display: none;
  }

  [data-theme="dark"] & {
    border-color: transparent;
    background: var(--color-surface-3);
    box-shadow: 0 6px 14px rgba(0, 0, 0, 0.45);
  }
`;

/* Mobile floating bulk-action bar — frosted water-glass instead of dark navy */
export const BigRowMobile = styled.div`
  display: none;
  position: fixed;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  width: calc(100% - 40px);
  z-index: 10;
  padding: 12px 14px;
  align-items: center;
  justify-content: space-between;
  border-radius: 22px;
  border: 1px solid var(--glass-edge);
  background:
    radial-gradient(
      120% 60% at 30% -10%,
      rgba(var(--glass-white-rgb), 0.94) 0%,
      rgba(var(--glass-pale-rgb), 0.86) 60%,
      rgba(var(--glass-pale-rgb), 0.78) 100%
    ),
    linear-gradient(
      180deg,
      rgba(var(--glass-white-rgb), 0.92) 0%,
      rgba(var(--glass-pale-rgb), 0.86) 100%
    );
  /* Floating mobile bar — fixed position over scrolling content. blur
     lowered from 24px to 12px (it sits over fast-scrolling cards, so
     keeping a higher radius would tank scroll FPS on phones). */
  backdrop-filter: blur(12px) saturate(180%);
  -webkit-backdrop-filter: blur(12px) saturate(180%);
  box-shadow:
    0 24px 48px -16px rgba(var(--color-brand-500-rgb), 0.36),
    0 8px 20px rgba(var(--color-brand-400-rgb), 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.95),
    inset 0 -6px 14px rgba(var(--color-brand-200-rgb), 0.24);

  @media (max-width: 768px) {
    display: flex;
  }

  [data-theme="dark"] & {
    border-color: transparent;
    background: var(--color-surface-3);
    box-shadow:
      0 16px 32px -12px rgba(0, 0, 0, 0.6),
      0 6px 14px rgba(0, 0, 0, 0.32);
  }
`;

/* Download — brand-gradient water button with white text */
export const DownloadButton = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 38px;
  padding: 0 16px;
  border-radius: var(--radius-pill);
  border: 1px solid rgba(var(--glass-white-rgb), 0.72);
  background:
    radial-gradient(
      140% 100% at 20% 0%,
      rgba(255, 255, 255, 0.3) 0%,
      rgba(255, 255, 255, 0) 55%
    ),
    linear-gradient(
      180deg,
      var(--color-brand-400) 0%,
      var(--color-brand-600) 55%,
      var(--color-brand-700) 100%
    );
  box-shadow:
    0 10px 22px rgba(var(--color-brand-600-rgb), 0.3),
    0 2px 6px rgba(var(--color-brand-600-rgb), 0.14),
    inset 0 1px 0 rgba(255, 255, 255, 0.55),
    inset 0 -3px 6px rgba(var(--color-brand-700-rgb), 0.28);
  color: var(--glass-text-on-pill);
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 700;
  letter-spacing: -0.005em;
  cursor: pointer;
  outline: none;
  overflow: hidden;
  white-space: nowrap;
  transition:
    transform 200ms var(--ease-snappy),
    filter 200ms var(--ease-snappy),
    box-shadow 200ms var(--ease-snappy);

  /* Top sheen */
  &::before {
    content: "";
    position: absolute;
    top: 2px;
    left: 10px;
    right: 10px;
    height: 8px;
    border-radius: 999px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.7) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(1px);
    pointer-events: none;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  svg {
    width: 20px;
    height: 20px;
    position: relative;
    z-index: 1;
    path {
      stroke: ${(p) => (p.$white ? "white" : "unset")};
    }
  }

  &:hover {
    transform: translateY(-1px);
    filter: brightness(1.06);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    height: 36px;
    font-size: 13px;
    padding: 0 14px;
  }

  [data-theme="dark"] & {
    border-color: transparent;
  }
`;

/* Delete — water-themed danger button (soft red frosted glass) */
export const DeleteButton = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 38px;
  padding: 0 14px;
  border-radius: var(--radius-pill);
  border: 1px solid rgba(255, 120, 143, 0.45);
  background: radial-gradient(
    120% 100% at 20% 0%,
    rgba(255, 240, 243, 0.95) 0%,
    rgba(255, 220, 228, 0.78) 55%,
    rgba(255, 205, 216, 0.68) 100%
  );
  box-shadow:
    0 6px 14px rgba(255, 96, 122, 0.22),
    inset 0 1px 0 rgba(255, 255, 255, 0.85),
    inset 0 -2px 6px rgba(255, 180, 195, 0.28);
  cursor: pointer;
  outline: none;
  transition:
    transform 200ms var(--ease-snappy),
    border-color 200ms var(--ease-snappy),
    box-shadow 200ms var(--ease-snappy);

  /* Top sheen */
  &::before {
    content: "";
    position: absolute;
    top: 1px;
    left: 10px;
    right: 10px;
    height: 8px;
    border-radius: 999px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.65) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(1px);
    pointer-events: none;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  svg {
    width: 18px;
    height: 18px;
    margin-bottom: 3px;
    position: relative;
    z-index: 1;
    path {
      stroke: #d34763;
    }
  }

  &:hover {
    transform: translateY(-1px);
    border-color: rgba(255, 96, 122, 0.65);
    box-shadow:
      0 10px 20px rgba(255, 96, 122, 0.32),
      inset 0 1px 0 rgba(255, 255, 255, 1),
      inset 0 -2px 6px rgba(255, 180, 195, 0.4);
  }

  &:active {
    transform: translateY(0);
  }

  [data-theme="dark"] & {
    border-color: rgba(255, 120, 143, 0.32);
    background: rgba(211, 71, 99, 0.18);
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.4);

    svg path {
      stroke: #ff7896;
    }

    &:hover {
      background: rgba(211, 71, 99, 0.26);
      border-color: rgba(255, 120, 143, 0.5);
      box-shadow: 0 6px 14px rgba(0, 0, 0, 0.5);
    }
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
    filter: grayscale(0.4);
    pointer-events: none;
  }
`;

/* Select All / filter button — frosted water pill (neutral state) */
export const CollectionButton = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: fit-content;
  min-height: 38px;
  padding: 0 16px;
  border-radius: var(--radius-pill);
  border: 1px solid
    ${({ $selected }) =>
      $selected
        ? "rgba(var(--color-brand-400-rgb), 0.55)"
        : "rgba(var(--glass-white-rgb), 0.65)"};
  background: ${({ $selected }) =>
    $selected
      ? `radial-gradient(
           140% 100% at 20% 0%,
           rgba(255, 255, 255, 0.3) 0%,
           rgba(255, 255, 255, 0) 55%
         ),
         linear-gradient(180deg, var(--color-brand-400) 0%, var(--color-brand-600) 55%, var(--color-brand-700) 100%)`
      : `radial-gradient(
           120% 100% at 20% 0%,
           rgba(var(--glass-white-rgb), 0.95) 0%,
           rgba(var(--glass-pale-rgb), 0.78) 55%,
           rgba(var(--glass-pale-rgb), 0.65) 100%
         )`};
  box-shadow: ${({ $selected }) =>
    $selected
      ? `0 10px 22px rgba(var(--color-brand-600-rgb), 0.3),
         inset 0 1px 0 rgba(255, 255, 255, 0.55),
         inset 0 -3px 6px rgba(var(--color-brand-700-rgb), 0.28)`
      : `0 6px 14px rgba(var(--color-brand-500-rgb), 0.1),
         inset 0 1px 0 rgba(255, 255, 255, 0.9),
         inset 0 -2px 6px rgba(var(--color-brand-200-rgb), 0.18)`};
  cursor: pointer;
  outline: none;
  transition:
    transform 220ms var(--ease-snappy),
    border-color 220ms var(--ease-snappy),
    box-shadow 220ms var(--ease-snappy);

  &::before {
    content: "";
    position: absolute;
    top: 1px;
    left: 12px;
    right: 12px;
    height: 8px;
    border-radius: 999px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, ${({ $selected }) => ($selected ? 0.5 : 0.6)}) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(1px);
    pointer-events: none;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  &:hover {
    transform: translateY(-1px);
    border-color: rgba(var(--color-brand-400-rgb), 0.5);
  }

  &:active {
    transform: translateY(0);
  }

  [data-theme="dark"] & {
    border-color: ${({ $selected }) =>
      $selected ? "transparent" : "transparent"};
    background: ${({ $selected }) =>
      $selected
        ? `radial-gradient(140% 100% at 20% 0%, rgba(255, 255, 255, 0.18) 0%, rgba(255, 255, 255, 0) 55%),
           linear-gradient(180deg, var(--color-brand-400) 0%, var(--color-brand-600) 55%, var(--color-brand-700) 100%)`
        : "transparent"};
    box-shadow: ${({ $selected }) =>
      $selected
        ? "0 6px 14px rgba(0, 0, 0, 0.45), inset 0 -3px 6px rgba(var(--color-brand-700-rgb), 0.32)"
        : "none"};
  }
`;

/* ─── Grid layout ────────────────────────────────────────────────────────── */

export const Content = styled.div`
  display: flex;
  flex-wrap: wrap;
  padding: 0 4px;
  flex: 1;
  gap: 12px;

  @media (max-width: 768px) {
    gap: 8px;
  }
  @media (max-width: 480px) {
    justify-content: center;
  }
`;

export const Column = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
  z-index: 5;
  @media (max-width: 768px) {
    gap: 8px;
  }
`;

/* ─── Empty state — water-glass panel (was dark navy) ─────────────────── */

export const EmptyState = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 56px 32px;
  width: 100%;
  max-width: 860px;
  min-height: 320px;
  margin: 48px auto;
  text-align: center;
  border-radius: var(--radius-xl);
  border: 1px solid var(--glass-edge);
  background:
    radial-gradient(
      120% 60% at 30% -10%,
      rgba(var(--glass-white-rgb), 0.95) 0%,
      rgba(var(--glass-pale-rgb), 0.86) 55%,
      rgba(var(--glass-pale-rgb), 0.76) 100%
    ),
    linear-gradient(
      180deg,
      rgba(var(--glass-white-rgb), 0.9) 0%,
      rgba(var(--glass-pale-rgb), 0.84) 100%
    );
  /* Empty state panel — single blur kept, lowered from 26px to 12px.
     emptyStateFloat brightness keyframe dropped — ran continuously even
     when the empty state was off-screen. */
  backdrop-filter: blur(12px) saturate(180%);
  -webkit-backdrop-filter: blur(12px) saturate(180%);
  box-shadow:
    0 28px 56px -18px rgba(var(--color-brand-500-rgb), 0.36),
    0 10px 24px rgba(var(--color-brand-400-rgb), 0.18),
    inset 0 2px 0 rgba(255, 255, 255, 0.95),
    inset 0 -8px 20px rgba(var(--color-brand-200-rgb), 0.24);

  [data-theme="dark"] & {
    border-color: transparent;
    box-shadow:
      0 24px 48px -16px rgba(0, 0, 0, 0.55),
      0 8px 18px rgba(0, 0, 0, 0.32),
      inset 0 -8px 20px rgba(var(--color-brand-700-rgb), 0.16);
    &::after {
      opacity: 0;
    }
  }
  overflow: hidden;
  isolation: isolate;

  /* Top specular sheen */
  &::before {
    content: "";
    position: absolute;
    top: 2px;
    left: 32px;
    right: 32px;
    height: 18px;
    border-radius: 999px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.85) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(2px);
    pointer-events: none;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  /* Bottom aqua underglow */
  &::after {
    content: "";
    position: absolute;
    bottom: -6px;
    left: 25%;
    right: 25%;
    height: 14px;
    background: radial-gradient(
      ellipse at center top,
      rgba(var(--color-brand-400-rgb), 0.4) 0%,
      rgba(var(--color-brand-400-rgb), 0) 70%
    );
    pointer-events: none;
    z-index: -1;
  }

  h3 {
    margin-bottom: 12px;
    font-family: var(--font-family-primary);
    font-size: 28px;
    font-weight: 800;
    line-height: normal;
    letter-spacing: -0.02em;

    /* Brand gradient heading */
    background: linear-gradient(
      0deg,
      var(--color-text-strong) 0%,
      var(--color-text-body) 45%,
      var(--color-brand-600) 100%
    );
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    color: transparent;
    filter: drop-shadow(0 1px 2px rgba(var(--color-brand-600-rgb), 0.14));
  }

  p {
    margin-bottom: 24px;
    max-width: 460px;
    color: var(--color-text-body);
    font-family: var(--font-family-primary);
    font-size: 16px;
    font-weight: 500;
    line-height: normal;
    letter-spacing: -0.005em;
  }

  [data-theme="dark"] & {
    border-color: transparent;
  }
`;

/* ─── Primary Go Explore / Login button — brand-gradient water ────────── */

export const Button = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: ${(props) => (props.$width ? "100%" : "180px")};
  min-height: 46px;
  padding: 0 22px;
  border-radius: var(--radius-pill);
  border: 1px solid rgba(var(--glass-white-rgb), 0.72);
  background:
    radial-gradient(
      140% 100% at 20% 0%,
      rgba(255, 255, 255, 0.3) 0%,
      rgba(255, 255, 255, 0) 55%
    ),
    linear-gradient(
      180deg,
      var(--color-brand-400) 0%,
      var(--color-brand-600) 55%,
      var(--color-brand-700) 100%
    );
  box-shadow:
    0 14px 28px rgba(var(--color-brand-600-rgb), 0.32),
    0 3px 8px rgba(var(--color-brand-600-rgb), 0.14),
    inset 0 1px 0 rgba(255, 255, 255, 0.6),
    inset 0 -3px 6px rgba(var(--color-brand-700-rgb), 0.28);

  [data-theme="dark"] & {
    border-color: transparent;
    border: none;
    box-shadow:
      0 8px 18px rgba(0, 0, 0, 0.5),
      0 3px 8px rgba(0, 0, 0, 0.3),
      inset 0 -3px 6px rgba(0, 0, 0, 0.22);
  }

  color: var(--glass-text-on-pill);
  font-family: var(--font-family-primary);
  font-size: 15px;
  font-weight: 700;
  letter-spacing: -0.005em;
  text-shadow: 0 1px 1px rgba(var(--color-brand-900-rgb), 0.28);
  cursor: pointer;
  outline: none;
  overflow: hidden;
  transition:
    transform 220ms var(--ease-snappy),
    filter 220ms var(--ease-snappy),
    box-shadow 220ms var(--ease-snappy);

  /* Top sheen */
  &::before {
    content: "";
    position: absolute;
    top: 2px;
    left: 14px;
    right: 14px;
    height: 10px;
    border-radius: 999px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.72) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(1.5px);
    pointer-events: none;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    filter: brightness(1.06);
    [data-theme="dark"] &:hover {
      border-color: transparent;
      border: none;
      box-shadow:
        0 8px 18px rgba(0, 0, 0, 0.5),
        0 3px 8px rgba(0, 0, 0, 0.3),
        inset 0 -3px 6px rgba(0, 0, 0, 0.22);
    }
    box-shadow:
      0 18px 34px rgba(var(--color-brand-600-rgb), 0.44),
      inset 0 1px 0 rgba(255, 255, 255, 0.7),
      inset 0 -3px 8px rgba(var(--color-brand-700-rgb), 0.38);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
    box-shadow: none;
  }

  [data-theme="dark"] & {
    border-color: transparent;
  }
`;
